/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import './NotificationBell.css';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  useEffect(() => {
    let notificationSystem = null;

    const setupNotificationSystem = (system) => {
      notificationSystem = system;

      // Check if already connected on mount
      if (notificationSystem.isReady()) {
        setIsConnected(true);
      }

      // Set up callback for when connection becomes ready
      notificationSystem.onConnectionReady = () => {
        setIsConnected(true);
      };

      notificationSystem.onInitialNotifications = (notificationsArray) => {
        const sorted = [...notificationsArray].sort(
          (a, b) => (b.creacion_ts || 0) - (a.creacion_ts || 0),
        );

        setNotifications(sorted);

        const unread = sorted.filter((n) => !n.read).length;
        setUnreadCount(unread);
      };

      notificationSystem.onNewNotification = (notification) => {
        setNotifications((prev) => {
          const updated = [notification, ...prev].sort(
            (a, b) => (b.creacion_ts || 0) - (a.creacion_ts || 0),
          );
          return updated;
        });

        setUnreadCount((prev) => {
          if (!notification.read) {
            return prev + 1;
          }
          return prev;
        });
      };

      notificationSystem.onNotificationUpdated = (notification) => {
        setNotifications((prevNotifications) => {
          const index = prevNotifications.findIndex((n) => n.notification_id === notification.notification_id);

          if (index !== -1) {
            const wasUnread = prevNotifications[index].read && !notification.read;

            prevNotifications[index] = notification;

            if (wasUnread) {
              setUnreadCount((prev) => Math.max(0, prev + 1));
            }

            return prevNotifications.sort((a, b) => (b.creacion_ts || 0) - (a.creacion_ts || 0));
          }

          return prevNotifications;
        });
      };

      notificationSystem.onNotificationRemoved = (notificationId) => {
        setNotifications((prevNotifications) => {
          const index = prevNotifications.findIndex((n) => n.notification_id === notificationId);

          if (index !== -1) {
            const wasUnread = !prevNotifications[index].read;

            if (wasUnread) {
              setUnreadCount((prev) => Math.max(0, prev - 1));
            }

            return [...prevNotifications.slice(0, index), ...prevNotifications.slice(index + 1)];
          }

          return prevNotifications;
        });
      };

      notificationSystem.onNotificationRead = (notification) => {
        setUnreadCount((prev) => {
          if (notification && notification.notification_id) {
            const notif = notificationSystem.notifications.get(notification.notification_id);
            if (notif && notif.read) {
              return Math.max(0, prev - 1);
            }
          }
          return prev;
        });
      };

      // Try to load any existing notifications (in case they arrived before React mounted)
      if (notificationSystem.notifications && notificationSystem.getNotifications().size > 0) {
        const sorted = Array.from(notificationSystem.getNotifications().values()).sort(
          (a, b) => (b.creacion_ts || 0) - (a.creacion_ts || 0),
        );
        setNotifications(sorted);

        const unread = sorted.filter((n) => !n.read).length;
        setUnreadCount(unread);
      }
    };

    // Wait for the custom element to be defined, then find it
    customElements.whenDefined('notification-system').then(() => {
      const system = document.querySelector('notification-system');
      if (system) {
        setupNotificationSystem(system);
      }
    });

    // Handle clicks outside dropdown
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up trigger functions
      if (notificationSystem) {
        notificationSystem.onConnectionReady = null;
        notificationSystem.onInitialNotifications = null;
        notificationSystem.onNewNotification = null;
        notificationSystem.onNotificationUpdated = null;
        notificationSystem.onNotificationRemoved = null;
      }
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const markAsRead = (notificationId) => {
    const notificationSystem = document.querySelector('notification-system');
    if (!notificationSystem) return;

    notificationSystem.markAsRead(notificationId);
  };

  const markAllAsRead = () => {
    const notificationSystem = document.querySelector('notification-system');
    if (!notificationSystem) return;

    notificationSystem.markAllAsRead();

    setUnreadCount(0);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Justo ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;

    return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'error':
        return 'var(--orsan-red)';
      case 'warning':
        return 'var(--orsan-orange)';
      case 'success':
        return 'var(--orsan-green)';
      default:
        return 'var(--orsan-blue)';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'error':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="notif-icon"
          >
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="notif-icon"
          >
            <path
              fillRule="evenodd"
              d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'success':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="notif-icon"
          >
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="notif-icon"
          >
            <path
              fillRule="evenodd"
              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 0 1 .67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 1 1-.671-1.34l.041-.022ZM12 9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  return (
    <div className="notification-bell-wrapper">
      <button
        ref={bellRef}
        className="bell-button"
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          className="bell-icon"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
          />
        </svg>
        {!isConnected ? (
          <span className="bell-badge loading">...</span>
        ) : (
          unreadCount > 0 && (
            <span className="bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="notification-dropdown">
          <div className="dropdown-header">
            <span className="dropdown-title">Notificaciones</span>
            {isConnected && unreadCount > 0 && (
              <button className="mark-all-read" onClick={markAllAsRead}>
                Marcar todas como leídas
              </button>
            )}
          </div>

          <div className="notifications-list">
            {!isConnected ? (
              <div className="loading-state">
                <svg className="loading-spinner" viewBox="0 0 24 24">
                  <circle
                    className="spinner-track"
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    strokeWidth="4"
                  />
                  <circle
                    className="spinner-circle"
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    strokeWidth="4"
                  />
                </svg>
                <div className="loading-text">Cargando notificaciones...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="empty-state">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="empty-icon"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.143 17.082a24.248 24.248 0 0 0 3.844.148m-3.844-.148a23.856 23.856 0 0 1-5.455-1.31 8.964 8.964 0 0 0 2.3-5.542m3.155 6.852a3 3 0 0 0 5.667 1.97m1.965-2.277L21 21m-4.225-4.225a23.81 23.81 0 0 0 3.536-1.003A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6.53 6.53m10.245 10.245L6.53 6.53M3 3l3.53 3.53"
                  />
                </svg>
                <div className="empty-text">No hay notificaciones</div>
              </div>
            ) : (
              notifications.map((notification) => {
                return (
                  <div
                    key={notification.notification_id}
                    className={`notification-item ${notification.read ? '' : 'unread'}`}
                    onClick={() => {
                      if (!notification.read) {
                        markAsRead(notification.notification_id);
                      }

                      if (notification.redirect_to) {
                        window.location.href = notification.redirect_to;
                      }
                    }}
                  >
                    <div style={{ color: getPriorityColor(notification.priority) }}>
                      {getPriorityIcon(notification.priority)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-header">
                        {notification.title && (
                          <div className="notification-title">{notification.title}</div>
                        )}
                        {!notification.read && <div className="unread-dot" />}
                      </div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">
                        {formatTimestamp(notification.start_time || notification.creacion_ts)}
                      </div>
                    </div>
                    {notification.redirect_to && (
                      <svg
                        className="notification-redirect-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.72 7.72a.75.75 0 0 1 1.06 0l3.75 3.75a.75.75 0 0 1 0 1.06l-3.75 3.75a.75.75 0 1 1-1.06-1.06l2.47-2.47H3a.75.75 0 0 1 0-1.5h16.19l-2.47-2.47a.75.75 0 0 1 0-1.06Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
